import React, { useState, useMemo, useEffect } from 'react';
import type { CoinData } from '../types/interfaces';
import MarketStats from './MarketStats';
import FilterControls from './FilterControls';
import CoinList from './CoinList';
import { CoinListSkeleton, MarketStatsSkeleton } from './LoadingSkeleton';

interface MarketTabProps {
  cryptoData: CoinData[];
  loading: boolean;
  error: string | null;
  onCoinClick: (coinId: string) => void;
  searchQuery: string;
  sortBy: string;
  onSortByChange: (sortBy: string) => void;
  isInWatchlist?: (coinId: string) => boolean;
  onWatchlistToggle?: (coinId: string) => void;
  currency?: string;
}

export default function MarketTab({ cryptoData, loading, error, onCoinClick, searchQuery, sortBy, onSortByChange, isInWatchlist, onWatchlistToggle, currency = 'usd' }: MarketTabProps) {
  const [minPrice, setMinPrice] = useState<number>(0);
  const [minMarketCap, setMinMarketCap] = useState<number>(100000000);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(25);

  const filteredAndSorted = useMemo(() => {
    // Deduplicate coins by ID first
    const uniqueCoins = cryptoData.reduce((acc, coin) => {
      if (!acc.find(c => c.id === coin.id)) {
        acc.push(coin);
      }
      return acc;
    }, [] as CoinData[]);
    
    let filtered = uniqueCoins.filter(coin => {
      // Filter by type (utility = BSV only) when sortBy is 'utility'
      if (sortBy === 'utility') {
        const isBSV = coin.id === 'bitcoin-sv' || 
                      coin.symbol.toLowerCase() === 'bsv' ||
                      coin.name.toLowerCase().includes('bitcoin sv');
        if (!isBSV) return false;
      }
      
      const matchesMarketCap = (coin.market_cap || 0) >= minMarketCap;
      const matchesPrice = (coin.current_price || 0) >= minPrice;
      const matchesSearch = searchQuery === '' || 
        coin.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        coin.symbol.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesMarketCap && matchesPrice && matchesSearch;
    });

    // Only sort if not using utility filter (utility filter shows only BSV, no sorting needed)
    if (sortBy !== 'utility') {
      filtered.sort((a, b) => {
        switch (sortBy) {
          case 'price':
            return (b.current_price || 0) - (a.current_price || 0);
          case 'name':
            return a.name.localeCompare(b.name);
          case 'change24h':
            return (b.price_change_percentage_24h || 0) - (a.price_change_percentage_24h || 0);
          case 'market_cap':
          default:
            return (b.market_cap || 0) - (a.market_cap || 0);
        }
      });
    }

    return filtered;
  }, [cryptoData, minMarketCap, minPrice, sortBy, searchQuery]);

  // Reset to page 1 when filters or search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, minPrice, minMarketCap, sortBy]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAndSorted.slice(start, start + itemsPerPage);
  }, [filteredAndSorted, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSorted.length / itemsPerPage);

  if (loading) {
    return (
      <div>
        <MarketStatsSkeleton />
        <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-6 mb-8 flex gap-6 flex-wrap items-end shadow-bubble border-2 border-primary/10">
          <div className="flex flex-col gap-3 flex-1 min-w-[180px]">
            <div className="h-4 w-24 bg-bg-tertiary rounded animate-pulse" />
            <div className="h-12 w-full bg-bg-tertiary rounded-full animate-pulse" />
          </div>
          <div className="flex flex-col gap-3 flex-1 min-w-[180px]">
            <div className="h-4 w-32 bg-bg-tertiary rounded animate-pulse" />
            <div className="h-12 w-full bg-bg-tertiary rounded-full animate-pulse" />
          </div>
          <div className="flex flex-col gap-3 flex-1 min-w-[180px]">
            <div className="h-4 w-36 bg-bg-tertiary rounded animate-pulse" />
            <div className="h-12 w-full bg-bg-tertiary rounded-full animate-pulse" />
          </div>
        </div>
        <CoinListSkeleton count={10} />
      </div>
    );
  }

  // Show error banner if there's an error, but still show data if available
  const showErrorBanner = error && cryptoData.length === 0;
  
  if (showErrorBanner) {
    const isRateLimit = error.toLowerCase().includes('rate limit') || error.toLowerCase().includes('429');
    return (
      <div className="text-center py-20 px-5">
        <div className="text-6xl mb-6">{isRateLimit ? '⏱️' : '😞'}</div>
        <div className={`text-xl mb-4 font-bold ${isRateLimit ? 'text-yellow-400' : 'text-red-400'}`}>
          {isRateLimit ? '⏱️ Rate Limited' : '❌ Error'}
        </div>
        <div className="text-text-secondary mb-6 bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-6 border-2 border-primary/10 shadow-bubble">
          {error}
        </div>
        {isRateLimit && (
          <div className="text-sm text-text-secondary/70 mt-4">
            The free CoinGecko API has strict rate limits. Please wait 2-3 minutes and refresh the page.
          </div>
        )}
      </div>
    );
  }
  
  // Show warning banner if error but we have cached data
  const showWarningBanner = error && cryptoData.length > 0;

  return (
    <div>
      {showWarningBanner && (
        <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-2 border-yellow-500/50 rounded-3xl p-5 mb-8 text-yellow-400 text-sm font-medium shadow-bubble">
          ⚠️ {error} Data shown is from cache.
        </div>
      )}
      
      <MarketStats data={cryptoData} currency={currency} />
      
      <FilterControls
        sortBy={sortBy}
        onSortChange={onSortByChange}
        minPrice={minPrice}
        onMinPriceChange={setMinPrice}
        minMarketCap={minMarketCap}
        onMinMarketCapChange={setMinMarketCap}
      />

      <div className="text-center text-text-secondary mb-4 text-sm">
        Showing {paginatedData.length} of {filteredAndSorted.length} coins
        {searchQuery && ` matching "${searchQuery}"`}
      </div>

      <CoinList
        coins={paginatedData}
        onCoinClick={(coinId) => {
          console.log('MarketTab: Coin clicked:', coinId);
          onCoinClick(coinId);
        }}
        isInWatchlist={isInWatchlist}
        onWatchlistToggle={onWatchlistToggle}
        currency={currency}
      />

      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-6 flex-wrap">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-bg-secondary rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-text-secondary">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-bg-secondary rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

